import * as React from 'react'

import { DotLoader } from '@procurify/ui'
import { Redirect } from 'react-router-dom'

import { Page } from '@webapp/core/components/Page'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useIntegrationDetailsPage } from './useIntegrationDetailsPage'
import { type IntegrationNameEnum } from '../../entities/enums/IntegrationNameEnum'

const IntegrationDetailsChunk = React.lazy(() =>
  import('../../feature-sets/IntegrationDetailsView').then(
    ({ IntegrationDetailsView }) => ({
      default: IntegrationDetailsView,
    })
  )
)

export interface IIntegrationDetailsPageProps {
  platform: IntegrationNameEnum
}

export const IntegrationDetailsPage = ({
  platform,
}: IIntegrationDetailsPageProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const {
    data: { integration, integrationLabel },
    states: { isLoading, isError },
  } = useIntegrationDetailsPage({ platform })

  const pageTitle =
    !integration && !isError
      ? intl.formatMessage(messages.loading)
      : intl.formatMessage(messages.setupPageTitle, {
          integrationLabel,
        })

  const breadcrumbs = [
    {
      link: url('SettingsPage'),
      name: intl.formatMessage(messages.settingsTitle),
    },
    {
      link: url('IntegrationsPage'),
      name: intl.formatMessage(messages.integrationListTitle),
    },
    {
      name: pageTitle,
      escapeCaseFormatting: true,
    },
  ]

  const renderContent = () => {
    if (isLoading) {
      return <DotLoader />
    }

    if (isError || !integration) {
      return <Redirect to={url('IntegrationsPage')} />
    }

    return (
      <React.Suspense fallback={<DotLoader />}>
        <IntegrationDetailsChunk integration={integration} />
      </React.Suspense>
    )
  }

  return (
    <Page pageTitle={pageTitle} breadcrumbs={breadcrumbs}>
      {renderContent()}
    </Page>
  )
}
